import { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Search, Package, Trash2, ChevronDown, Image, ImageOff, Eye,
  ToggleLeft, ToggleRight, Plus, X, Save, Pencil,
  ChevronRight, ChevronLeft, Store, Tag, DollarSign, ListChecks,
  Upload,
} from 'lucide-react'
import { ROUTES } from '@/constants/routes'
import { productsService } from '@/services/productsService'
import { categoriesService } from '@/services/categoriesService'
import { vendorsService } from '@/services/vendorsService'
import { attributesService } from '@/services/attributesService'
import { shippingService } from '@/services/shippingService'
import { useDebounce } from '@/hooks/useDebounce'
import { usePagination } from '@/hooks/usePagination'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import EmptyState from '@/components/shared/EmptyState'
import Pagination from '@/components/shared/Pagination'
import StatusBadge from '@/components/shared/StatusBadge'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import SearchableSelect from '@/components/shared/SearchableSelect'
import ProductImagesManager from '@/components/shared/ProductImagesManager'
import ProductVariantsManager from '@/components/shared/ProductVariantsManager'
import RichTextEditor from '@/components/shared/RichTextEditor'
import { formatCurrency, getLocalizedField } from '@/utils/formatters'

// ── Helpers ───────────────────────────────────────────────────────────────────
const EMPTY_FORM = {
  vendorId: '', categoryId: '',
  nameAr: '', nameEn: '',
  descAr: '', descEn: '',
  price: '', comparePrice: '', shippingClassId: '', sku: '',
  stockQuantity: '', lowStockThreshold: 5,
  status: 'active', tags: '',
  images: [], variants: [],
}

function DialogOverlay({ children, onClose }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 50,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
    }}>
      <div onClick={(e) => e.stopPropagation()} className="animate-in" style={{
        background: '#fff', borderRadius: 24, padding: 0,
        width: '100%', maxWidth: 560,
        boxShadow: '0 32px 64px rgba(0,0,0,0.2)',
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        position: 'relative', overflow: 'hidden',
      }}>
        <button onClick={onClose} style={{
          position: 'absolute', top: 16, left: 16, zIndex: 1,
          width: 32, height: 32, borderRadius: 10,
          background: '#F3F3F3', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#767683',
        }}>
          <X size={16} />
        </button>
        {children}
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#454652', marginBottom: 5, fontFamily: 'Tajawal, sans-serif' }}>
        {label}
      </label>
      {children}
    </div>
  )
}

// ── Attribute Field ───────────────────────────────────────────────────────────
function AttributeField({ def, value, onChange }) {
  const label = def.label?.ar || def.label?.en || def.key
  const unit  = def.unit ? ` (${def.unit})` : ''

  const baseStyle = {
    width: '100%', padding: '8px 12px', borderRadius: 10,
    border: '1.5px solid #EBEBEB', fontSize: 13, fontFamily: 'Cairo, sans-serif',
    fontWeight: 600, color: '#1A1C1C', background: '#FAFAFA', outline: 'none',
    boxSizing: 'border-box',
  }

  let input
  if (def.type === 'select' && def.options?.length) {
    input = (
      <select style={baseStyle} value={value} onChange={e => onChange(e.target.value)}>
        <option value="">— اختر —</option>
        {def.options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    )
  } else if (def.type === 'boolean') {
    input = (
      <select style={baseStyle} value={value} onChange={e => onChange(e.target.value)}>
        <option value="">— اختر —</option>
        <option value="true">نعم</option>
        <option value="false">لا</option>
      </select>
    )
  } else if (def.type === 'multi_select' && def.options?.length) {
    const selected = value ? value.split(',').map(v => v.trim()).filter(Boolean) : []
    input = (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {def.options.map(opt => {
          const active = selected.includes(opt)
          return (
            <button key={opt} type="button"
              onClick={() => {
                const next = active ? selected.filter(v => v !== opt) : [...selected, opt]
                onChange(next.join(', '))
              }}
              style={{
                padding: '4px 10px', borderRadius: 8, fontSize: 12, cursor: 'pointer',
                fontFamily: 'Cairo, sans-serif', fontWeight: 600, transition: 'all 0.15s',
                border: `1.5px solid ${active ? '#000666' : '#EBEBEB'}`,
                background: active ? 'rgba(0,6,102,0.08)' : '#FAFAFA',
                color: active ? '#000666' : '#767683',
              }}
            >{opt}</button>
          )
        })}
      </div>
    )
  } else if (def.type === 'range') {
    input = <input type="number" style={{ ...baseStyle, direction: 'ltr', textAlign: 'left' }}
      value={value} onChange={e => onChange(e.target.value)} placeholder="0" />
  } else {
    input = <input type="text" style={baseStyle} value={value}
      onChange={e => onChange(e.target.value)} placeholder={`أدخل ${label}`} />
  }

  return (
    <div>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#454652', marginBottom: 5, fontFamily: 'Tajawal, sans-serif' }}>
        {label}{unit}{def.isRequired && <span style={{ color: '#BA1A1A', marginRight: 3 }}>*</span>}
      </label>
      {input}
    </div>
  )
}

// ── Product Form Steps ─────────────────────────────────────────────────────────
const PRODUCT_STEPS = [
  { id: 1, label: 'البائع والفئة', icon: Store },
  { id: 2, label: 'المعلومات',    icon: Tag },
  { id: 3, label: 'التسعير',      icon: DollarSign },
  { id: 4, label: 'المواصفات',    icon: ListChecks },
  { id: 5, label: 'المتغيرات',    icon: Package },
  { id: 6, label: 'الوصف والصورة', icon: Image },
]

function ProductStepIndicator({ currentStep, totalSteps }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, padding: '18px 28px 0', direction: 'rtl' }}>
      {PRODUCT_STEPS.map((step, i) => {
        const done   = currentStep > step.id
        const active = currentStep === step.id
        const Icon   = step.icon
        return (
          <div key={step.id} style={{ display: 'flex', alignItems: 'center', flex: i < totalSteps - 1 ? 1 : 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 10,
                background: done ? '#1B6D24' : active ? '#000666' : '#F3F3F3',
                color: done || active ? '#fff' : '#767683',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s', fontSize: 13,
              }}>
                {done ? '✓' : <Icon size={15} />}
              </div>
              <span style={{ fontSize: 9, fontWeight: 700, fontFamily: 'Cairo, sans-serif', color: active ? '#000666' : done ? '#1B6D24' : '#767683', whiteSpace: 'nowrap' }}>
                {step.label}
              </span>
            </div>
            {i < totalSteps - 1 && (
              <div style={{ flex: 1, height: 2, background: done ? '#1B6D24' : '#F3F3F3', margin: '0 5px', marginBottom: 18, transition: 'background 0.3s' }} />
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── Product Form Dialog ────────────────────────────────────────────────────────
export function ProductFormDialog({ mode, initialData, flatCats, onClose, onSuccess }) {
  const { t } = useTranslation()
  const [form, setForm]               = useState(initialData || EMPTY_FORM)
  const [step, setStep]               = useState(1)
  const isCreate = mode === 'create'
  const [attrValues, setAttrValues]   = useState({})
  const [attrDefs, setAttrDefs]       = useState([])
  const [attrLoading, setAttrLoading] = useState(false)
  // Server-side vendor search state
  const [vendorSearch, setVendorSearch] = useState('')
  const [selectedVendor, setSelectedVendor] = useState(null) // preserve label after search clears
  const debouncedVendorSearch           = useDebounce(vendorSearch, 350)
  const qc = useQueryClient()

  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }))
  const setVal = (key, val) => setForm((p) => ({ ...p, [key]: val }))

  // Sync images and variants when parent fetches full product data
  useEffect(() => {
    if (!isCreate && initialData) {
      setForm((prev) => ({ 
        ...prev, 
        images: initialData.images || [],
        variants: initialData.variants || [] 
      }))
    }
  }, [initialData?.images, initialData?.variants])

  // Sync attrValues when initialData.attributes arrives (async in edit mode)
  useEffect(() => {
    if (initialData?.attributes?.length) {
      setAttrValues(Object.fromEntries(initialData.attributes.map(a => [a.key, a.value])))
    }
  }, [initialData?.attributes])

  // Fetch attribute definitions when category changes
  useEffect(() => {
    if (!form.categoryId) { setAttrDefs([]); return }
    let cancelled = false
    setAttrLoading(true)
    attributesService.getByCategory(form.categoryId)
      .then(defs => { if (!cancelled) setAttrDefs(defs || []) })
      .catch(() => { if (!cancelled) setAttrDefs([]) })
      .finally(() => { if (!cancelled) setAttrLoading(false) })
    return () => { cancelled = true }
  }, [form.categoryId])

  const STEPS = isCreate ? PRODUCT_STEPS : PRODUCT_STEPS.slice(1)
  const totalSteps = STEPS.length

  // ── Server-side vendor fetch (only when dialog is open + create mode) ──────
  const { data: vendorsData, isFetching: vendorsLoading } = useQuery({
    queryKey: ['vendors-search', debouncedVendorSearch],
    queryFn: () => vendorsService.getAll({
      status: 'approved',
      search: debouncedVendorSearch || undefined,
      limit: 20,
    }),
    enabled: isCreate,
    staleTime: 30_000,
  })
  const vendorOptions = (vendorsData?.data || []).map((v) => ({
    value: v.id,
    label: v.storeName?.ar || v.storeName?.en || v.id,
    sublabel: v.user?.name || v.city || '',
  }))

  const { data: shippingClassesData } = useQuery({
    queryKey: ['shipping-classes'],
    queryFn: shippingService.getClasses,
    staleTime: 60_000,
  })
  const shippingClasses = (shippingClassesData || []).filter((c) => c.isActive)

  const createMut = useMutation({
    mutationFn: async () => {
      const product = await productsService.adminCreate({
        vendorId: form.vendorId,
        categoryId: form.categoryId || undefined,
        name: { ar: form.nameAr, en: form.nameEn },
        description: { ar: form.descAr, en: form.descEn },
        price: parseFloat(form.price),
        comparePrice: form.comparePrice ? parseFloat(form.comparePrice) : undefined,
        shippingClassId: form.shippingClassId || undefined,
        sku: form.sku || undefined,
        stockQuantity: parseInt(form.stockQuantity) || 0,
        lowStockThreshold: parseInt(form.lowStockThreshold) || 5,
        status: form.status,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        images: form.images,
        variants: form.variants,
      })
      const attrs = Object.fromEntries(Object.entries(attrValues).filter(([, v]) => v !== ''))
      if (Object.keys(attrs).length > 0) {
        await attributesService.updateProductAttributes(product.id, { attributes: attrs })
      }
      return product
    },
    onSuccess: () => {
      toast.success('تم إنشاء المنتج بنجاح')
      qc.invalidateQueries({ queryKey: ['products'] })
      onSuccess?.()
    },
    onError: (e) => toast.error(e?.response?.data?.message || 'فشل إنشاء المنتج'),
  })

  const updateMut = useMutation({
    mutationFn: async () => {
      await productsService.adminUpdate(initialData.id, {
        categoryId: form.categoryId || undefined,
        name: { ar: form.nameAr, en: form.nameEn },
        description: { ar: form.descAr, en: form.descEn },
        price: parseFloat(form.price),
        comparePrice: form.comparePrice ? parseFloat(form.comparePrice) : undefined,
        shippingClassId: form.shippingClassId || null,
        sku: form.sku || undefined,
        stockQuantity: parseInt(form.stockQuantity) || 0,
        lowStockThreshold: parseInt(form.lowStockThreshold) || 5,
        status: form.status,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        images: form.images,
        // (Variants are updated individually on the fly in edit mode, but we can send them here if backend supports it. Better to let ProductVariantsManager handle edit mode directly)
      })
      const attrs = Object.fromEntries(Object.entries(attrValues).filter(([, v]) => v !== ''))
      if (Object.keys(attrs).length > 0) {
        await attributesService.updateProductAttributes(initialData.id, { attributes: attrs })
      }
    },
    onSuccess: () => {
      toast.success('تم تحديث المنتج')
      qc.invalidateQueries({ queryKey: ['products'] })
      onSuccess?.()
    },
    onError: (e) => toast.error(e?.response?.data?.message || 'فشل تحديث المنتج'),
  })

  const isPending = createMut.isPending || updateMut.isPending

  // Category options for SearchableSelect
  const catOptions = [
    { value: '', label: 'بدون فئة' },
    ...flatCats.map((c) => ({ value: c.id, label: c.name })),
  ]

  const step1Valid = isCreate ? !!form.vendorId : true
  const step2Valid = !!form.nameAr
  const step3Valid = !!form.price
  const attrStepValid = attrDefs.filter(d => d.isRequired).every(d => (attrValues[d.key] || '').trim())
  const currentStepValid = () => {
    if (isCreate) {
      if (step === 1) return step1Valid
      if (step === 2) return step2Valid
      if (step === 3) return step3Valid
      if (step === 4) return attrStepValid
      return true
    } else {
      if (step === 1) return step2Valid
      if (step === 2) return step3Valid
      if (step === 3) return attrStepValid
      return true
    }
  }

  const isLastStep = step === totalSteps

  return (
    <DialogOverlay onClose={onClose}>
      {/* Step indicator — show for create only (edit is short) */}
      {isCreate && <ProductStepIndicator currentStep={step} totalSteps={PRODUCT_STEPS.length} />}

      <div style={{ padding: isCreate ? '16px 28px 0' : '28px 28px 0' }}>
        <h3 style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 800, fontSize: 17, color: '#000666', marginBottom: 4, paddingRight: 4 }}>
          {isCreate ? 'إضافة منتج جديد' : 'تعديل المنتج'}
        </h3>
        <p style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 12, color: '#767683', marginBottom: 16 }}>
          {isCreate && step === 1 && 'اختر البائع والفئة'}
          {(isCreate ? step === 2 : step === 1) && 'أدخل اسم المنتج والحالة'}
          {(isCreate ? step === 3 : step === 2) && 'السعر والمخزون'}
          {(isCreate ? step === 4 : step === 3) && 'مواصفات المنتج'}
          {(isCreate ? step === 5 : step === 4) && 'متغيرات المنتج'}
          {(isCreate ? step === 6 : step === 5) && 'الوصف والوسوم والصورة'}
        </p>
      </div>

      <div style={{ padding: '0 28px', overflowY: 'auto', maxHeight: '52vh' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, paddingBottom: 8 }}>

          {/* ── Create Step 1 — Vendor (server search) + Category ── */}
          {isCreate && step === 1 && (
            <>
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label="البائع *">
                  <SearchableSelect
                    options={vendorOptions}
                    value={form.vendorId}
                    selectedOption={selectedVendor}
                    onChange={(val, opt) => {
                      setVal('vendorId', val)
                      setSelectedVendor(opt || null)
                    }}
                    onSearchChange={setVendorSearch}
                    isLoading={vendorsLoading}
                    placeholder="ابحث عن البائع باسم المتجر..."
                    searchPlaceholder="اكتب اسم المتجر أو المالك..."
                    clearable
                  />
                </Field>
                {!form.vendorId && (
                  <p style={{ fontSize: 11, color: '#767683', marginTop: 5, fontFamily: 'Tajawal, sans-serif' }}>
                    ابحث عن البائع بالاسم — يظهر البائعون المعتمدون فقط
                  </p>
                )}
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label="الفئة">
                  <SearchableSelect
                    options={catOptions}
                    value={form.categoryId}
                    onChange={(val) => setVal('categoryId', val)}
                    placeholder="اختر الفئة (اختياري)"
                    searchPlaceholder="ابحث عن فئة..."
                    clearable
                  />
                </Field>
              </div>
            </>
          )}

          {/* ── Step 2 (create) / Step 1 (edit) — Names + Status */}
          {((isCreate && step === 2) || (!isCreate && step === 1)) && (
            <>
              <Field label="اسم المنتج (عربي) *">
                <input className="hg-input" placeholder="اسم المنتج بالعربية" value={form.nameAr} onChange={set('nameAr')} />
              </Field>
              <Field label="Product Name (English)">
                <input className="hg-input" placeholder="Product name in English" value={form.nameEn} onChange={set('nameEn')} dir="ltr" />
              </Field>
              {!isCreate && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <Field label="الفئة">
                    <SearchableSelect
                      options={catOptions}
                      value={form.categoryId}
                      onChange={(val) => setVal('categoryId', val)}
                      placeholder="اختر الفئة (اختياري)"
                      searchPlaceholder="ابحث عن فئة..."
                      clearable
                    />
                  </Field>
                </div>
              )}
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label="الحالة">
                  <div className="relative">
                    <select className="hg-input" style={{ paddingLeft: 28 }} value={form.status} onChange={set('status')}>
                      <option value="active">نشط</option>
                      <option value="draft">مسودة</option>
                      <option value="inactive">غير نشط</option>
                      <option value="archived">مؤرشف</option>
                    </select>
                    <ChevronDown size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#767683', pointerEvents: 'none' }} />
                  </div>
                </Field>
              </div>
            </>
          )}

          {/* ── Step 3 (create) / Step 2 (edit) — Pricing + Inventory */}
          {((isCreate && step === 3) || (!isCreate && step === 2)) && (
            <>
              <Field label="السعر *">
                <input className="hg-input" type="number" placeholder="0.00" min="0" step="0.01" value={form.price} onChange={set('price')} dir="ltr" />
              </Field>
              <Field label="السعر قبل الخصم">
                <input className="hg-input" type="number" placeholder="0.00" min="0" step="0.01" value={form.comparePrice} onChange={set('comparePrice')} dir="ltr" />
              </Field>
              <Field label="فئة الشحن">
                <div className="relative">
                  <select className="hg-input" style={{ paddingLeft: 28 }} value={form.shippingClassId} onChange={set('shippingClassId')}>
                    <option value="">بدون شحن (شحن مجاني)</option>
                    {shippingClasses.map((c) => (
                      <option key={c.id} value={c.id}>
                        {(c.name?.ar || c.name?.en) + ` — ${c.baseCost} ج.م` + (Number(c.extraUnitCost) > 0 ? ` (+${c.extraUnitCost}/قطعة زيادة)` : '')}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#767683', pointerEvents: 'none' }} />
                </div>
              </Field>
              <Field label="رمز SKU">
                <input className="hg-input" placeholder="APL-IP15PRO" value={form.sku} onChange={set('sku')} dir="ltr" />
              </Field>
              <Field label="الكمية في المخزون">
                <input className="hg-input" type="number" min="0" placeholder="0" value={form.stockQuantity} onChange={set('stockQuantity')} dir="ltr" />
              </Field>
            </>
          )}

          {/* ── Step 4 (create) / Step 3 (edit) — Attributes */}
          {((isCreate && step === 4) || (!isCreate && step === 3)) && (
            <div style={{ gridColumn: '1 / -1' }}>
              {attrLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '24px 0', color: '#767683' }}>
                  <LoadingSpinner size={16} />
                  <span style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 13 }}>جارٍ تحميل المواصفات...</span>
                </div>
              ) : attrDefs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: '#767683' }}>
                  <ListChecks size={32} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                  <p style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 13 }}>
                    {form.categoryId ? 'لا توجد مواصفات إضافية لهذه الفئة' : 'اختر فئة في الخطوة الأولى لإضافة المواصفات'}
                  </p>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  {attrDefs.map(def => (
                    <AttributeField
                      key={def.id}
                      def={def}
                      value={attrValues[def.key] || ''}
                      onChange={(v) => setAttrValues(prev => ({ ...prev, [def.key]: v }))}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Step 5 (create) / Step 4 (edit) — Variants */}
          {((isCreate && step === 5) || (!isCreate && step === 4)) && (
            <div style={{ gridColumn: '1 / -1' }}>
               <ProductVariantsManager 
                  variants={form.variants} 
                  onChange={(v) => setVal('variants', v)} 
                  productId={initialData?.id} 
                  isCreate={isCreate}
               />
            </div>
          )}

          {/* ── Step 6 (create) / Step 5 (edit) — Description + Tags + Image */}
          {((isCreate && step === 6) || (!isCreate && step === 5)) && (
            <>
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label={t('products.descArLabel') || 'الوصف (عربي)'}>
                  <RichTextEditor value={form.descAr} onChange={(html) => setVal('descAr', html)} placeholder={t('products.descArPlaceholder') || 'وصف المنتج بالعربية'} dir="rtl" />
                </Field>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label={t('products.descEnLabel') || 'Description (English)'}>
                  <RichTextEditor value={form.descEn} onChange={(html) => setVal('descEn', html)} placeholder={t('products.descEnPlaceholder') || 'Product description in English'} dir="ltr" />
                </Field>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label="الوسوم (مفصولة بفاصلة)">
                  <input className="hg-input" placeholder="مثال: أبل، هاتف، جوال" value={form.tags} onChange={set('tags')} />
                </Field>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <ProductImagesManager
                  images={form.images}
                  onChange={(imgs) => setVal('images', imgs)}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{ padding: '16px 28px', borderTop: '1px solid #F3F3F3', display: 'flex', gap: 10 }}>
        {step > 1 && (
          <button
            type="button"
            onClick={() => setStep((s) => s - 1)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 12, fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13, background: '#F3F3F3', color: '#454652', border: 'none', cursor: 'pointer' }}
          >
            <ChevronRight size={15} />
            السابق
          </button>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={onClose} style={{ padding: '10px 16px', borderRadius: 12, fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13, background: '#F3F3F3', color: '#454652', border: 'none', cursor: 'pointer' }}>
          إلغاء
        </button>
        {!isLastStep ? (
          <button
            type="button"
            onClick={() => setStep((s) => s + 1)}
            disabled={!currentStepValid()}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', borderRadius: 12, fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13, background: '#000666', color: '#fff', border: 'none', cursor: !currentStepValid() ? 'not-allowed' : 'pointer', opacity: !currentStepValid() ? 0.5 : 1, boxShadow: '0 4px 14px rgba(0,6,102,0.2)' }}
          >
            التالي
            <ChevronLeft size={15} />
          </button>
        ) : (
          <button
            onClick={() => isCreate ? createMut.mutate() : updateMut.mutate()}
            disabled={isPending || !currentStepValid()}
            className="flex items-center gap-2"
            style={{ padding: '10px 20px', borderRadius: 12, fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13, background: '#1B6D24', color: '#fff', border: 'none', cursor: isPending ? 'not-allowed' : 'pointer', opacity: isPending ? 0.6 : 1, boxShadow: '0 4px 16px rgba(27,109,36,0.25)' }}
          >
            <Save size={14} />
            {isPending ? 'جارٍ الحفظ...' : isCreate ? 'إنشاء المنتج' : 'حفظ التعديلات'}
          </button>
        )}
      </div>
    </DialogOverlay>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ProductsPage() {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const qc   = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const brandId = searchParams.get('brandId') || ''

  const [search,    setSearch]    = useState('')
  const [catFilter, setCatFilter] = useState('')
  const [status,    setStatus]    = useState('')
  const [featured,  setFeatured]  = useState('')
  const { page, limit, setPage }  = usePagination(1, 20)
  const debouncedSearch = useDebounce(search, 400)

  const [deleteDialog, setDeleteDialog] = useState({ open: false, product: null })
  const [formDialog,   setFormDialog]   = useState({ open: false, mode: 'create', product: null })

  // ── Queries ───────────────────────────────────────────────────────────────
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['products', { search: debouncedSearch, catFilter, status, featured, page, limit, brand: brandId }],
    queryFn: () => productsService.getAll({
      search:     debouncedSearch || undefined,
      categoryId: catFilter       || undefined,
      brand:      brandId         || undefined,
      status:     status          || undefined,
      isFeatured: featured === 'featured' ? true : featured === 'normal' ? false : undefined,
      page,
      limit,
    }),
  })

  const { data: catTree } = useQuery({
    queryKey: ['categories-tree'],
    queryFn: categoriesService.getTree,
  })

  const products = data?.data || []
  const meta     = data?.meta || {}

  // Flatten category tree for filter dropdown
  const flatCats = []
  const flatten = (arr, depth = 0) => {
    arr?.forEach((c) => {
      flatCats.push({ id: c.id, name: (depth > 0 ? '— ' : '') + (c.name?.[lang] || c.name?.ar || c.name?.en || '') })
      if (c.children?.length) flatten(c.children, depth + 1)
    })
  }
  flatten(catTree)

  // ── Mutations ─────────────────────────────────────────────────────────────
  const featureMut = useMutation({
    mutationFn: (id) => productsService.toggleFeature(id),
    onSuccess: () => { toast.success('تم تحديث التمييز'); qc.invalidateQueries({ queryKey: ['products'] }) },
    onError: () => toast.error('حدث خطأ'),
  })

  const deleteMut = useMutation({
    mutationFn: (id) => productsService.delete(id),
    onSuccess: () => {
      toast.success('تم حذف المنتج')
      qc.invalidateQueries({ queryKey: ['products'] })
      setDeleteDialog({ open: false, product: null })
    },
    onError: () => toast.error('فشل الحذف'),
  })

  const openEdit = async (product) => {
    // Open dialog immediately with list data so there's no delay
    const base = {
      id: product.id,
      vendorId: product.vendor?.id || '',
      categoryId: product.category?.id || '',
      nameAr: product.name?.ar || '',
      nameEn: product.name?.en || '',
      descAr: product.description?.ar || '',
      descEn: product.description?.en || '',
      price: product.price || '',
      comparePrice: product.comparePrice || '',
      shippingClassId: product.shippingClassId || product.shippingClass?.id || '',
      sku: product.sku || '',
      stockQuantity: product.stockQuantity ?? '',
      lowStockThreshold: product.lowStockThreshold || 5,
      status: product.status || 'active',
      tags: (product.tags || []).join(', '),
      variants: product.variants || [],
      images: (product.images || []).map((img, i) => ({
        url: img.url,
        altText: img.altText || '',
        isPrimary: img.isPrimary ?? i === 0,
        sortOrder: img.sortOrder ?? i,
      })),
    }
    setFormDialog({ open: true, mode: 'edit', product: base })
    // Then fetch full product to get ALL images (list only returns primary image)
    try {
      const full = await productsService.getById(product.id)
      const fullImages = (full.images || []).map((img, i) => ({
        url: img.url,
        altText: img.altText || '',
        isPrimary: img.isPrimary ?? i === 0,
        sortOrder: img.sortOrder ?? i,
      }))
      const attributes = (full.attributes || []).map(a => ({
        key: a.definition?.key ?? a.key,
        value: a.value,
      }))
      setFormDialog((prev) => ({
        ...prev,
        product: { ...prev.product, images: fullImages, attributes, variants: full.variants || [] },
      }))
    } catch {
      // keep the list data as fallback
    }
  }

  const handleInlineSave = async (productId, patch) => {
    await productsService.adminUpdate(productId, patch)
    qc.invalidateQueries({ queryKey: ['products'] })
    toast.success('تم الحفظ')
  }

  const handleFilterChange = (setter) => (e) => { setter(e.target.value); setPage(1) }

  return (
    <div className="space-y-5">

      {/* ── Filter bar ─────────────────────────────────────────────────────── */}
      <div className="hg-card p-4 flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-52">
          <Search size={16} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#767683', pointerEvents: 'none' }} />
          <input className="hg-input" style={{ paddingRight: 40 }}
            placeholder="ابحث باسم المنتج..."
            value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }} />
        </div>

        {/* Category filter */}
        <div className="relative">
          <select className="hg-input cursor-pointer min-w-40" style={{ paddingLeft: 28 }}
            value={catFilter} onChange={handleFilterChange(setCatFilter)}>
            <option value="">كل الفئات</option>
            {flatCats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <ChevronDown size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#767683', pointerEvents: 'none' }} />
        </div>

        {/* Status filter */}
        <div className="relative">
          <select className="hg-input cursor-pointer min-w-36" style={{ paddingLeft: 28 }}
            value={status} onChange={handleFilterChange(setStatus)}>
            <option value="">كل الحالات</option>
            <option value="active">نشط</option>
            <option value="draft">مسودة</option>
            <option value="inactive">غير نشط</option>
            <option value="archived">مؤرشف</option>
          </select>
          <ChevronDown size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#767683', pointerEvents: 'none' }} />
        </div>

        {/* Featured filter */}
        <div className="relative">
          <select className="hg-input cursor-pointer min-w-32" style={{ paddingLeft: 28 }}
            value={featured} onChange={handleFilterChange(setFeatured)}>
            <option value="">الكل</option>
            <option value="featured">مميز فقط</option>
            <option value="normal">غير مميز</option>
          </select>
          <ChevronDown size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#767683', pointerEvents: 'none' }} />
        </div>

        {meta.total != null && (
          <span className="px-3 py-1.5 rounded-xl text-sm font-bold"
            style={{ background: 'rgba(0,6,102,0.06)', color: '#000666', fontFamily: 'Cairo, sans-serif' }}>
            {meta.total?.toLocaleString('ar-EG')} منتج
          </span>
        )}

        <button
          onClick={() => navigate(ROUTES.PRODUCTS_IMPORT)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold flex-shrink-0 transition-all hover:bg-[#D5D5D5]"
          style={{ background: '#E2E2E2', color: '#374151', fontFamily: 'Cairo, sans-serif' }}
        >
          <Upload size={16} />
          {t('products.import') || 'استيراد'}
        </button>

        <button
          onClick={() => setFormDialog({ open: true, mode: 'create', product: null })}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white flex-shrink-0"
          style={{ background: '#000666', fontFamily: 'Cairo, sans-serif' }}
        >
          <Plus size={16} />
          منتج جديد
        </button>
      </div>

      {/* ── Products Table ─────────────────────────────────────────────────── */}
      <div className="hg-card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20"><LoadingSpinner /></div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <p style={{ color: '#BA1A1A', fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>فشل تحميل المنتجات</p>
            <button onClick={() => refetch()} className="hg-btn-navy px-5 py-2 text-sm">إعادة المحاولة</button>
          </div>
        ) : products.length === 0 ? (
          <EmptyState icon={Package} message="لا توجد منتجات بهذه المعايير" />
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#F9F9F9', borderBottom: '2px solid #F3F3F3' }}>
                    {['المنتج', 'البائع', 'الفئة', 'السعر', 'المخزون', 'الحالة', 'مميز', 'الإجراءات'].map((h) => (
                      <th key={h} style={{
                        textAlign: 'right', padding: '12px 14px',
                        fontSize: 11, fontWeight: 700, color: '#767683',
                        whiteSpace: 'nowrap', fontFamily: 'Tajawal, sans-serif',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {products.map((product, idx) => (
                    <ProductRow
                      key={product.id}
                      product={product}
                      lang={lang}
                      last={idx === products.length - 1}
                      onFeature={() => featureMut.mutate(product.id)}
                      onEdit={() => openEdit(product)}
                      onViewDetails={() => navigate('/products/' + product.id)}
                      onDelete={() => setDeleteDialog({ open: true, product })}
                      onInlineSave={(patch) => handleInlineSave(product.id, patch)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding: '0 16px 16px' }}>
              <Pagination page={page} totalPages={meta.totalPages} total={meta.total} limit={limit} onPageChange={setPage} />
            </div>
          </>
        )}
      </div>

      {/* ── Form Dialog ────────────────────────────────────────────────────── */}
      {formDialog.open && (
        <ProductFormDialog
          mode={formDialog.mode}
          initialData={formDialog.product}
          flatCats={flatCats}
          onClose={() => setFormDialog({ open: false, mode: 'create', product: null })}
          onSuccess={() => setFormDialog({ open: false, mode: 'create', product: null })}
        />
      )}

      {/* ── Delete confirm ─────────────────────────────────────────────────── */}
      <ConfirmDialog
        open={deleteDialog.open}
        title="حذف المنتج"
        message={`هل تريد حذف "${getLocalizedField(deleteDialog.product?.name, lang)}"؟ لا يمكن التراجع.`}
        variant="danger"
        loading={deleteMut.isPending}
        onConfirm={() => deleteMut.mutate(deleteDialog.product.id)}
        onCancel={() => setDeleteDialog({ open: false, product: null })}
      />
    </div>
  )
}

// ── Inline-editable table cell ────────────────────────────────────────────────
// Click the displayed value → becomes an input → Enter/blur saves, Escape cancels.
function InlineEditCell({ value, type = 'text', onSave, dir = 'rtl', minWidth = 80, children }) {
  const [editing,  setEditing]  = useState(false)
  const [inputVal, setInputVal] = useState('')
  const [saving,   setSaving]   = useState(false)
  const inputRef  = useRef(null)
  const cancelRef = useRef(false)

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editing])

  const start = () => {
    setInputVal(String(value ?? ''))
    cancelRef.current = false
    setEditing(true)
  }

  const commit = async () => {
    const raw = inputVal.trim()
    // No change — just close
    if (raw === String(value ?? '')) { setEditing(false); return }
    setSaving(true)
    setEditing(false)
    try {
      await onSave(raw)
    } catch (e) {
      toast.error(e?.response?.data?.message || 'فشل الحفظ')
    }
    setSaving(false)
  }

  const handleBlur    = () => { if (cancelRef.current) { cancelRef.current = false; return } commit() }
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { cancelRef.current = true; setEditing(false) }
    if (e.key === 'Enter')  commit()
  }

  if (saving) return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '2px 0' }}>
      <LoadingSpinner size={13} />
    </div>
  )

  if (editing) return (
    <input
      ref={inputRef}
      type={type === 'number' ? 'number' : 'text'}
      value={inputVal}
      onChange={(e) => setInputVal(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      dir={dir}
      style={{
        background: '#E2E2E2',
        border: 'none',
        borderRadius: 8,
        padding: '4px 8px',
        fontSize: 13,
        fontFamily: 'Cairo, sans-serif',
        fontWeight: 700,
        color: '#1A1C1C',
        outline: '2px solid rgba(0,6,102,0.3)',
        minWidth,
        width: '100%',
        boxSizing: 'border-box',
      }}
    />
  )

  return (
    <button
      onClick={start}
      title="اضغط للتعديل"
      style={{ background: 'none', border: 'none', cursor: 'text', padding: 0, textAlign: 'inherit', display: 'block', width: '100%' }}
    >
      {children}
    </button>
  )
}

// ── Product Row ───────────────────────────────────────────────────────────────
function ProductRow({ product, lang, last, onFeature, onEdit, onDelete, onInlineSave, onViewDetails }) {
  const name    = getLocalizedField(product.name, lang)
  const catName = getLocalizedField(product.category?.name, lang)
  const vendor  = getLocalizedField(product.vendor?.storeName, lang)
  const img     = product.images?.find(i => i.isPrimary)?.url || product.images?.[0]?.url

  const stock      = product.stockQuantity ?? 0
  const low        = product.lowStockThreshold || 5
  const stockColor = stock <= 0 ? '#BA1A1A' : stock <= low ? '#FF6D00' : '#1B6D24'

  return (
    <tr
      style={{ borderBottom: last ? 'none' : '1px solid #F3F3F3', transition: 'background 0.12s' }}
      onMouseEnter={(e) => e.currentTarget.style.background = '#F9F9F9'}
      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
    >
      {/* Product name + image */}
      <td style={{ padding: '12px 14px', minWidth: 220 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, overflow: 'hidden', background: '#F3F3F3', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {img
              ? <img src={img} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <ImageOff size={14} style={{ color: '#C4C4C4' }} />
            }
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <InlineEditCell
              value={product.name?.ar || ''}
              onSave={(val) => onInlineSave({ name: { ar: val, en: product.name?.en || '' } })}
              minWidth={120}
            >
              <div style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13, color: '#1A1C1C', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>
                {name}
              </div>
            </InlineEditCell>
            <InlineEditCell
              value={product.sku || ''}
              dir="ltr"
              onSave={(val) => onInlineSave({ sku: val || undefined })}
              minWidth={80}
            >
              <div style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 11, color: '#767683' }}>
                SKU: {product.sku || '—'}
              </div>
            </InlineEditCell>
          </div>
        </div>
      </td>

      {/* Vendor */}
      <td style={{ padding: '12px 14px' }}>
        <span style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 12, color: '#454652', whiteSpace: 'nowrap', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
          {vendor || '—'}
        </span>
      </td>

      {/* Category */}
      <td style={{ padding: '12px 14px' }}>
        {catName ? (
          <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6, background: 'rgba(0,6,102,0.06)', color: '#000666', fontFamily: 'Tajawal, sans-serif', whiteSpace: 'nowrap' }}>
            {catName}
          </span>
        ) : <span style={{ color: '#C4C4C4' }}>—</span>}
      </td>

      {/* Price */}
      <td style={{ padding: '12px 14px', minWidth: 100 }}>
        <InlineEditCell
          value={product.price ?? ''}
          type="number"
          dir="ltr"
          onSave={(val) => onInlineSave({ price: parseFloat(val) || 0 })}
          minWidth={80}
        >
          <span style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 800, fontSize: 14, color: '#FF6D00' }}>
            {formatCurrency(product.price)}
          </span>
        </InlineEditCell>
        <InlineEditCell
          value={product.comparePrice || ''}
          type="number"
          dir="ltr"
          onSave={(val) => onInlineSave({ comparePrice: parseFloat(val) || undefined })}
          minWidth={80}
        >
          {product.comparePrice > product.price ? (
            <div style={{ fontFamily: 'Cairo, sans-serif', fontSize: 11, color: '#767683', textDecoration: 'line-through' }}>
              {formatCurrency(product.comparePrice)}
            </div>
          ) : (
            <div style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 11, color: '#C4C4C4' }}>+ سعر قبل</div>
          )}
        </InlineEditCell>
      </td>

      {/* Stock */}
      <td style={{ padding: '12px 14px', minWidth: 70 }}>
        <InlineEditCell
          value={product.stockQuantity ?? 0}
          type="number"
          dir="ltr"
          onSave={(val) => onInlineSave({ stockQuantity: parseInt(val) || 0 })}
          minWidth={50}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: stockColor, flexShrink: 0 }} />
            <span style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13, color: stockColor }}>{stock}</span>
          </div>
        </InlineEditCell>
      </td>

      {/* Status */}
      <td style={{ padding: '12px 14px' }}>
        <StatusBadge status={product.status} type="vendor" lang={lang} />
      </td>

      {/* Featured toggle */}
      <td style={{ padding: '12px 14px' }}>
        <button onClick={onFeature} title={product.isFeatured ? 'إلغاء التمييز' : 'تمييز المنتج'}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
          {product.isFeatured
            ? <ToggleRight size={24} style={{ color: '#FF6D00' }} />
            : <ToggleLeft  size={24} style={{ color: '#C4C4C4' }} />
          }
        </button>
      </td>

      {/* Actions */}
      <td style={{ padding: '12px 14px' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={onViewDetails} title="عرض التفاصيل"
            style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(0,6,102,0.07)', color: '#000666', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,6,102,0.15)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,6,102,0.07)'}>
            <Eye size={13} />
          </button>
          <button onClick={onEdit} title="تعديل سريع"
            style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(0,6,102,0.07)', color: '#000666', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,6,102,0.15)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,6,102,0.07)'}>
            <Pencil size={13} />
          </button>
          <button onClick={onDelete} title="حذف"
            style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(186,26,26,0.07)', color: '#BA1A1A', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(186,26,26,0.15)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(186,26,26,0.07)'}>
            <Trash2 size={13} />
          </button>
        </div>
      </td>
    </tr>
  )
}
